
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackPurchase } from '@/lib/fbPixel';

export const usePricingLogic = () => {
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setIsAuthenticated(true);
          setUserId(session.user.id);
          
          // Check if user already has an active subscription
          try {
            const { data: subscriptions, error } = await supabase
              .from('subscriptions')
              .select('*')
              .eq('user_id', session.user.id)
              .eq('status', 'active');
              
            console.log('Checking subscription status, found:', subscriptions?.length, 'active subscriptions');
              
            if (subscriptions && subscriptions.length > 0) {
              // User already has an active subscription, redirect to dashboard
              navigate('/dashboard', { replace: true });
              return;
            }
          } catch (error) {
            console.error('Error checking subscription:', error);
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };
    
    // Check if this is a payment success return
    if (location.search.includes('payment=success')) {
      // Track purchase event
      // Using default value since we don't have actual price info in URL params
      const planType = new URLSearchParams(location.search).get('plan') || 'subscription';
      let purchaseValue = 19.00; // Default monthly value
      
      // Set value based on plan type if available
      if (planType === 'monthly') purchaseValue = 19.00;
      else if (planType === 'annual') purchaseValue = 79.00;
      else if (planType === 'lifetime') purchaseValue = 199.00;
      
      // Track the purchase event
      trackPurchase(purchaseValue, 'USD', { 
        content_type: 'subscription',
        content_name: planType
      });
      
      toast({
        title: "Payment successful!",
        description: "Thank you for your purchase. Setting up your account...",
      });
      
      // Add a 3-second delay to allow time for the webhook to process
      // and redirect to dashboard with state to trigger confetti
      setTimeout(() => {
        navigate('/dashboard', { 
          replace: true, 
          state: { fromLogin: true } 
        });
      }, 3000);
      
      return; // Skip the rest of the effect since we're redirecting
    }
    
    // If not a payment success return, check auth normally
    checkAuth();
    
    // Show error message if payment was canceled
    if (location.search.includes('payment=canceled')) {
      toast({
        title: "Payment canceled",
        description: "Your payment was not completed. Please try again when you're ready.",
        variant: "destructive",
      });
      
      // Clean up the URL
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [navigate, location.search, toast]);

  const handleSelectPlan = async (plan: 'monthly' | 'annual' | 'lifetime') => {
    if (!isAuthenticated) {
      // If not authenticated, redirect to register page
      navigate('/register', { state: { returnTo: `/pricing?plan=${plan}` } });
      return;
    }
    
    if (!userId) {
      toast({
        title: "Authentication error",
        description: "Please try logging in again.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsProcessing(plan);
      toast({
        title: "Preparing checkout...",
        description: "Please wait while we set up your payment.",
      });
      
      // Get the current origin to send to the edge function
      const origin = window.location.origin;
      
      console.log('Calling create-checkout with:', { plan, userId, origin });
      
      // Call the Supabase Edge Function to create a checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan, userId, origin },
      });
      
      console.log('Response from create-checkout:', data, error);
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
      }
      
      if (!data) {
        console.error('No data returned from create-checkout');
        throw new Error('No data returned from checkout');
      }
      
      if (data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned:', data);
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      setIsProcessing(null);
      toast({
        title: "Checkout error",
        description: error.message || "There was a problem starting the checkout process. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    isLoadingUser,
    isAuthenticated,
    userId,
    isProcessing,
    handleSelectPlan
  };
};
